import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { createReadableClass, getColorWithAlpha } from '@elfsight/widget-utils';
import { useWavesurfer } from '@wavesurfer/react';
import { useSettings } from '@wu/widget-core';
import Hover from 'wavesurfer.js/dist/plugins/hover.js';
import { timeFormat } from '../../../../helpers';
import { useTheme } from '../../../common';
import { usePlayer } from '../../PlayerProvider';

interface WavesurferProps {
  decompressedPeaks: Float32Array[];
  hideTime: boolean;
}

const HOVER_PLUGIN_CONFIG = {
  lineWidth: 0,
  labelBackground: '#282828',
  labelColor: '#fff',
  labelSize: '11px',
  labelPreferLeft: false
} as const;

const WAVEFORM_CONFIG = {
  height: 40,
  cursorWidth: 0,
  barWidth: 2,
  barGap: 1,
  barRadius: 10,
  barHeight: 1,
  normalize: true,
  interact: true,
  dragToSeek: false,
  hideScrollbar: true,
  backend: 'WebAudio'
} as const;

export const Wavesurfer = ({
  decompressedPeaks,
  hideTime
}: WavesurferProps) => {
  const {
    seekTo,
    duration,
    progress: { playedSeconds }
  } = usePlayer();
  const { accentColor } = useSettings();
  const { textColor } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  const hoverPlugin = useMemo(() => Hover.create(HOVER_PLUGIN_CONFIG), []);

  const plugins = useMemo(() => [hoverPlugin], [hoverPlugin]);

  const { wavesurfer } = useWavesurfer({
    container: containerRef,
    peaks: decompressedPeaks,
    duration,
    waveColor: textColor,
    progressColor: accentColor,
    plugins,
    ...WAVEFORM_CONFIG
  });

  useEffect(() => {
    wavesurfer?.setOptions({ peaks: decompressedPeaks });
  }, [decompressedPeaks, wavesurfer]);

  useEffect(() => {
    if (wavesurfer && playedSeconds !== undefined) {
      wavesurfer.setTime(playedSeconds);
    }
  }, [wavesurfer, playedSeconds]);

  useEffect(() => {
    if (!wavesurfer) {
      return;
    }

    const handleInteraction = (time: number) => {
      if (duration > 0) {
        seekTo(time / duration);
      }
    };

    const unsubscribe = wavesurfer.on('interaction', handleInteraction);

    return unsubscribe;
  }, [wavesurfer, duration, seekTo]);

  return (
    <Wrapper>
      <Show when={!hideTime}>
        <TimeContainer $color={getColorWithAlpha(textColor, 0.7)}>
          <TimeDisplay id="time">{timeFormat(playedSeconds ?? 0)}</TimeDisplay>
          <DurationDisplay id="duration">
            {timeFormat(duration)}
          </DurationDisplay>
        </TimeContainer>
      </Show>
      <Container ref={containerRef} />
    </Wrapper>
  );
};

const TimeContainer = styled.div.attrs<{
  $color: string;
}>({
  className: createReadableClass({
    component: 'wavesurfer',
    element: 'time-container'
  })
})`
  display: flex;
  justify-content: space-between;
  width: 100%;
  color: ${({ $color }) => $color};
`;

const Wrapper = styled.div.attrs({
  className: createReadableClass({
    component: 'wavesurfer',
    element: 'wrapper'
  })
})`
  position: relative;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  width: 100%;

  & ::part(canvases) {
    opacity: 0.3;
  }

  & ::part(hover-label) {
    padding: 2px 9px;
    border-radius: 4px;
    color: #fff;
  }
`;

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'wavesurfer',
    element: 'container'
  })
})`
  width: 100%;
`;

const TimeDisplay = styled.div.attrs({
  className: createReadableClass({
    component: 'wavesurfer',
    element: 'time-display'
  })
})`
  z-index: 11;
  font-size: 11px;
  border-radius: 4px;
`;

const DurationDisplay = styled.div.attrs({
  className: createReadableClass({
    component: 'wavesurfer',
    element: 'duration-display'
  })
})`
  z-index: 11;
  font-size: 11px;
  border-radius: 4px;
`;
